import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isArray } from 'lodash';
import { TLeaf } from './TreeInput';
import FormGroup from './FormGroup';
import { TState } from '../../redux/store';
import { useCanI } from '../../redux/actions';
import { RInput } from './Input';

export type TAction = {
  title?: string;
  label?: string;
  level?: number;
  isChecked?: boolean;
  onChange?: any;
  isIndeterminate?: boolean;
  forcedAction?: boolean;
};

export type TField = {
  _id?: string;
  name_id?: string;
  parent_id?: string;
  type: string;
  typeFn?: (formData: Record<string, any>) => string;
  name?: string;
  label: string;
  required?: boolean;
  advised?: boolean;
  dated?: boolean;
  showTime?: boolean;
  disabled?: boolean;
  disabledFn?: (formData: Record<string, any>) => boolean;
  allOptions?: any[];
  currOption?: any;
  options?: {
    label: string;
    value: any;
    roles?: string[];
    color?: string;
    disabled?: boolean;
  }[];
  orderedOptions?: {
    label: string;
    value: any;
    roles?: string[];
    color?: string;
    disabled?: boolean;
  }[];
  optionsFn?: (formData: Record<string, any>) => {
    label: string;
    value: any;
    roles?: string[];
    color?: string;
    disabled?: boolean;
  }[];
  unity?: string;
  list?: TLeaf[];
  actions?: TAction[];
  selected?: any;
  addressFields?: Record<string, boolean>;
  group?: string;
  condition?: (formData: Record<string, any>) => boolean;
  placeholder?: string;
  isMulti?: boolean;
  noFormat?: boolean;
  withEmpty?: boolean;
  repeatFields?: string[];
  repeatFieldsTable?: string[];
  nbRepeatFieldsTable?: number;
  max?: number;
};

const getForm = (f: any, props: any) => {
  const { errors, formData, direction } = props;

  const { typeFn, type, labelFn, label, disabledFn, disabled } = f;

  const fieldDisabled = disabledFn?.(formData) || disabled;

  const name = (!f.baseName && f.name_id) || f.name || f._id || '';
  const i = (
    <RInput
      {...props}
      f={{ ...f, disabled: fieldDisabled }}
      isError={!!errors[name]}
    />
  );

  const fieldType = typeFn?.(formData) || type;
  const fieldLabel = labelFn?.(formData) || label;

  return (
    <FormGroup
      field={{
        ...f,
        type: fieldType,
        label: fieldLabel,
      }}
      input={i}
      isError={!!errors[name]}
      direction={direction}
    />
  );
};

export const useGetFields = () => {
  const modalType = useSelector((state: TState) => state.modalType);
  const isAdd = modalType === 'addEntry';

  const canI = useCanI();

  return (f: any[] = [], props: any) =>
    f.reduce((prev, c: any) => {
      const name_id = c.baseName || c.name_id || c.name || c._id || '';
      const isCustom = !c._id;

      return !isCustom &&
        name_id &&
        c.type !== 'title' &&
        !canI(name_id, isAdd ? 'create' : 'read') &&
        !canI(`${name_id}_street`, isAdd ? 'create' : 'read') &&
        !canI(`${name_id}_city`, isAdd ? 'create' : 'read') &&
        !canI(`${name_id}_postal`, isAdd ? 'create' : 'read') &&
        !canI(`${name_id}_dept`, isAdd ? 'create' : 'read')
        ? prev
        : [...prev, getForm(c, props)];
    }, []) as JSX.Element[];
};

const useForm = (
  fields: TField[] = [],
  formOptions?: {
    planable?: boolean;
    noRowData?: boolean;
    direction?: 'column' | 'row';
    onSubmit?: (x?: any, y?: any, z?: any) => Promise<any>;
    onChange?: (label: string) => void;
  },
) => {
  const modalType = useSelector((state: TState) => state.modalType);
  const rowData = useSelector((state: TState) => state.rowData);

  const { direction, noRowData, onSubmit, onChange } = formOptions || {};

  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Record<string, Record<string, any>>>({});
  const [formData, setFormData] = useState<Record<string, any>>(
    noRowData ? {} : rowData || {},
  );

  useEffect(() => {
    !noRowData && !/planning/i.test(modalType) && setFormData({});
    setErrors({});
  }, [modalType]);

  useEffect(() => {
    !noRowData && setFormData(rowData || {});
    setErrors({});
  }, [rowData]);

  const handleSubmit = async () => {
    const resp = await onSubmit?.(formData);
    return resp;
  };

  const handleOnChange = (label: string, e: any, forced?: boolean) => {
    setFormData((d) => ({
      ...d,
      [label]: forced ? e : isArray(e) && !e?.length ? null : (e ?? ''),
    }));
    onChange?.(label);
  };

  const handleReset = (name: string) => {
    setFormData((d: any) => ({
      ...d,
      [name]: null,
      [`${name}_street`]: ``,
      [`${name}_city`]: '',
      [`${name}_dept`]: '',
      [`${name}_postal`]: '',
      [`${name}_addressDescription`]: '',
    }));
  };

  const props = useMemo(
    () => ({
      formOptions,
      formData,
      errors,
      direction,
      setFormData,
      setPending,
      handleOnChange,
      handleReset,
      onChange,
    }),
    [formOptions, errors, formData],
  );

  const filteredFields = fields.filter(
    (field) => !field.condition || field.condition?.(formData),
  );

  const getFields = useGetFields();
  const Form = getFields(filteredFields, props);

  return {
    Form,
    formData,
    pending,
    errors,
    handleSubmit,
    setFormData,
    setErrors,
  };
};

export default useForm;
